import { ChevronDownIcon, ChevronRightIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Tag } from '@/components'
import { TimelineTicket } from '@/types/Homes'
import { TicketListRow } from '@/types/Ticket'

import { getPriorityVariant, getStatusTagClassName } from './Tickets.data'

type Props = {
  expandedIds?: Set<number>
  includeMarket: boolean
  onToggleExpand?: (id: number) => void
  ticket: (TicketListRow | TimelineTicket) & { _isChildRow?: boolean }
}

export const MobileTicketCard = ({ ticket, includeMarket, expandedIds, onToggleExpand }: Props) => {
  const { internalTicketStatus, subject, ticketType, priority, createdAt, lastCommentDatetime } = ticket
  const navigate = useNavigate()
  const isChildRow = ticket._isChildRow ?? false
  const isParent =
    'allowAsParent' in ticket && ticket.allowAsParent && 'childTickets' in ticket && !!ticket.childTickets?.length
  const isExpanded = expandedIds?.has(ticket.id) ?? false

  const handleClick = () => {
    navigate(`/tickets/${ticket.id}`)
  }

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleExpand?.(ticket.id)
  }

  const marketSegment = 'marketSegment' in ticket ? ticket.marketSegment?.name || '' : ''

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const createdDate = createdAt ? formatInTimeZone(createdAt, timezone, 'MMM. dd, yyyy, h:mmaaa') : '--'

  const lastComment = lastCommentDatetime
    ? formatInTimeZone(lastCommentDatetime, timezone, 'MMM. dd, yyyy, h:mmaaa')
    : '--'

  return (
    <BoxStyled
      className={isChildRow ? 'ml-4 border-l-2 border-action-primary' : ''}
      onClick={handleClick}
      onKeyDown={() => {}}
      role="presentation"
    >
      <div className="mb-6 flex w-full items-center justify-between">
        <div className="max-w-2/3 mr-2 flex max-h-10 flex-col overflow-hidden text-ellipsis">
          {isChildRow && <p className="text-p-xs text-tertiary">↳ Child ticket</p>}
          {!isChildRow && 'parentTicket' in ticket && ticket.parentTicket && (
            <p className="text-p-xs text-tertiary">Sub-ticket of #{ticket.parentTicket.id}</p>
          )}
          <p className="text-p-sm text-secondary">{subject}</p>
        </div>
        <div className="flex items-center gap-2">
          {isParent && (
            <div
              className="flex items-center gap-1 text-tertiary"
              onClick={handleToggleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleToggleClick(e as any)
              }}
              role="button"
              tabIndex={0}
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 shrink-0" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 shrink-0" />
              )}
              <span className="rounded-full bg-action-primary px-2 py-0.5 text-p-sm text-white">
                +{'childTickets' in ticket ? ticket.childTickets?.length : 0}
              </span>
            </div>
          )}
          <Tag className={`${getStatusTagClassName(internalTicketStatus)} text-p-sm`}>{internalTicketStatus}</Tag>
        </div>
      </div>
      <div className=" ticket-information grid grid-cols-4">
        <div className="border-r border-secondary">
          <h6>Type</h6>
          <p>{ticketType}</p>
        </div>
        <div className="border-r  pl-2 border-secondary">
          <h6>Owner</h6>
          <p>{ticket.assignee?.fullName || ''}</p>
        </div>
        {includeMarket ? (
          <div className="border-r  pl-2  border-secondary">
            <h6>Market</h6>
            <p>{marketSegment}</p>
          </div>
        ) : null}
        <div className="pl-2">
          <h6>Priority</h6>
          <p className={`${getPriorityVariant(priority)} `}>{priority}</p>
        </div>
      </div>
      <div className="mt-2 ticket-information grid grid-cols-2">
        <div className="border-r border-secondary">
          <h6>Created</h6>
          <p>{createdDate}</p>
        </div>
        <div className="pl-2">
          <h6>Last Comment</h6>
          <p>{lastComment}</p>
        </div>
      </div>
    </BoxStyled>
  )
}

const BoxStyled = styled.div`
  padding: 0.75rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 1rem;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.12);

  .ticket-information {
    div {
      display: flex;
      flex-direction: column;
    }

    h6 {
      color: #787573;
      font-size: var(--text-p-xs);
    }
    p {
      font-size: var(--text-p-xs);
      line-height: 1.8;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
`
